import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { formatAmount, advanceConsumption } from '../../Utils/billingoDocuments';

const asDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * EVERY ADVANCE ON THIS ORDER, AND WHETHER IT CAN STILL BE DEDUCTED.
 *
 * Sits on the REVIEW step, above the totals: this is the one place the
 * operator commits, and the one place "Create Számla" can be disabled. It is
 * deliberately NOT also rendered on the Lines step — the deduction rows there
 * already show exactly what will be deducted, which is true and complete, and
 * the same card in two places is clutter in a four-step wizard. What is
 * ABSENT from the Lines step is explained here, one click later, before
 * anything irreversible happens.
 *
 * Three states, and they are three because the right next action differs:
 *   deducted here      — the advance is netted off this document.
 *   already deducted   — a Számla we hold spent it; this document charges the
 *                        rest, which is correct.
 *   held by Billingo   — Billingo named it in a refusal and we hold no child.
 *                        `advanceBlockedReason` stops the document, because
 *                        issuing full price could invoice the trip twice.
 *
 * A different currency from the document's is its own case: a Billingo
 * document is single-currency, so such an advance is not deductible HERE and
 * saying nothing would read as a lost deduction.
 */
function BillingoAdvancesCard({ advances = [], currency = null }) {
  if (!advances.length) return null;

  const stateOf = (advance) => {
    const consumption = advanceConsumption(advance);
    if (consumption?.by === 'refused') {
      return {
        tone: 'danger',
        label: 'held by Billingo',
        detail: `Billingo refused this advance${consumption.at ? ` on ${asDate(consumption.at)}` : ''}`
          + ' because it already has a descendant document — one we do not hold.'
          + ' It cannot be deducted, and the Számla needs reconciling in Billingo first.',
      };
    }
    if (consumption?.by === 'deducted') {
      return {
        tone: 'warning',
        label: 'already used',
        detail: `Deducted by Számla ${consumption.number || '(unnamed)'}`
          + `${consumption.at ? ` on ${asDate(consumption.at)}` : ''}. It cannot be used again.`,
      };
    }
    if (currency && (advance.currency || 'EUR') !== currency) {
      return {
        tone: 'secondary',
        label: `${advance.currency} advance`,
        detail: `A Billingo document is single-currency, so a ${advance.currency} advance`
          + ` cannot be netted off a ${currency} Számla.`,
      };
    }
    return { tone: 'success', label: 'deducted here', detail: '' };
  };

  const states = advances.map((advance) => ({ advance, state: stateOf(advance) }));
  const deducting = states.filter(({ state }) => state.tone === 'success').length;

  return (
    <Card className="mb-3" data-testid="billingo-advances">
      <Card.Body>
        <div className="text-muted small mb-2">Advances on this order</div>
        {states.map(({ advance, state }) => {
          const spent = state.tone === 'warning' || state.tone === 'danger';
          return (
            <div
              key={advance.id || advance.number}
              className="py-2 border-bottom"
              data-testid="billingo-advance"
            >
              <div className="d-flex justify-content-between align-items-start gap-2">
                <span className={spent ? 'text-muted text-decoration-line-through' : ''}>
                  {advance.number || '—'}
                  <span className="text-muted small ms-2">Előlegszámla</span>
                </span>
                <span className="text-nowrap">
                  <Badge bg={state.tone} className="me-2">{state.label}</Badge>
                  <span className={spent ? 'text-muted' : 'fw-semibold'}>
                    {spent ? formatAmount(advance.total, advance.currency)
                      : `− ${formatAmount(advance.total, advance.currency)}`}
                  </span>
                </span>
              </div>
              {state.detail && (
                <div className="small text-muted mt-1" data-testid="billingo-advance-detail">
                  {state.detail}
                </div>
              )}
            </div>
          );
        })}
        {deducting === 0 && (
          <div className="small text-muted mt-2" data-testid="billingo-advances-none">
            No advance is deducted — this Számla charges the full amount.
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default BillingoAdvancesCard;
